import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Card, 
  Typography, 
  Tag, 
  Button,
  Row,
  Col,
  Tooltip,
  Modal,
  Descriptions,
  App
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  FilterOutlined
} from '@ant-design/icons';
import auditService from '../services/auditService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditTrail = () => {
  const { message } = App.useApp();
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    entityType: '',
    action: '',
    performedBy: '',
    dateRange: null,
    search: ''
  });
  const [detailsModal, setDetailsModal] = useState({
    visible: false,
    record: null
  });

  useEffect(() => {
    fetchAuditTrail();
  }, [pagination.current, pagination.pageSize]);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDateForAPI = (date, isEndDate = false) => {
    if (!date) return '';
    
    // Clone the date to avoid modifying the original
    const d = new Date(date);
    
    // Check if the user has selected a specific time (not midnight)
    const hasUserSelectedTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
    
    if (!hasUserSelectedTime) {
      // If no specific time was selected, use default time ranges
      if (isEndDate) {
        // For end dates, set time to 23:59:59.999 to include the entire day
        d.setHours(23, 59, 59, 999);
      } else {
        // For start dates, set time to 00:00:00.000 to include from beginning of day
        d.setHours(0, 0, 0, 0);
      }
    }
    // If user has selected a specific time, keep it as is
    
    const isoString = d.toISOString();
    console.log(`formatDateForAPI: ${isEndDate ? 'End' : 'Start'} date - Input: ${date}, Output: ${isoString}, UserSelectedTime: ${hasUserSelectedTime}`);
    
    return isoString;
  };

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      const filterParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };

      if (filters.dateRange && filters.dateRange.length === 2) {
        filterParams.startDate = formatDateForAPI(filters.dateRange[0], false); // Start of day
        filterParams.endDate = formatDateForAPI(filters.dateRange[1], true);   // End of day
      }

      const response = await auditService.getAuditTrail(filterParams);
      
      if (response.success && response.data.data) {
        // Ensure we always set an array
       // alert(`AuditTrail response data: ${JSON.stringify(response.data?.data?.data)}`); // Debugging line
        const auditArray = Array.isArray(response.data?.data?.data) ? response.data.data.data : [];
        setAuditData(auditArray);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      } else {
        message.error(response.message || 'Failed to fetch audit trail');
        setAuditData([]); // Reset to empty array on error
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      message.error('Failed to fetch audit trail');
      setAuditData([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchAuditTrail();
  };

  const handleReset = () => {
    setFilters({
      entityType: '',
      action: '',
      performedBy: '',
      dateRange: null,
      search: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(() => fetchAuditTrail(), 0);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'green';
      case 'CREATE-BULK': return 'cyan';
      case 'UPDATE': return 'blue';
      case 'DELETE': return 'red';
      default: return 'default';
    }
  };

  const getEntityTypeColor = (entityType) => {
    switch (entityType) {
      case 'User': return 'purple';
      case 'Group': return 'orange';
      case 'BusinessUnit': return 'cyan';
      default: return 'default';
    }
  };

  const showDetails = (record) => {
    setDetailsModal({
      visible: true,
      record
    });
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => formatDate(timestamp),
      sorter: true,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 200,
      render: (action) => (
        <Tag color={getActionColor(action)}>{action}</Tag>
      ),
      filters: [
        { text: 'CREATE', value: 'CREATE' },
        { text: 'CREATE-BULK', value: 'CREATE-BULK' },
        { text: 'UPDATE', value: 'UPDATE' },
        { text: 'DELETE', value: 'DELETE' }
      ]
    },
    {
      title: 'Entity Type',
      dataIndex: 'entityType',
      key: 'entityType',
      width: 120,
      render: (entityType) => (
        <Tag color={getEntityTypeColor(entityType)}>{entityType}</Tag>
      ),
      filters: [
        { text: 'User', value: 'User' },
        { text: 'Group', value: 'Group' },
        { text: 'BusinessUnit', value: 'BusinessUnit' }
      ]
    },
    {
      title: 'Entity',
      dataIndex: 'entityDescription',
      key: 'entity',
      width: 250,
      ellipsis: true,
      render: (entityDescription, record) => (
        <Tooltip title={entityDescription || record.entityId}>
          <span>
            {entityDescription || (
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>
                {record.entityId}
              </span>
            )}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Performed By',
      dataIndex: 'performedBy',
      key: 'performedBy',
      width: 200,
      render: (performedBy) => {
        if (typeof performedBy === 'object' && performedBy) {
          return `${performedBy.name} ${performedBy.surname}` || performedBy.email;
        }
        return performedBy || 'System';
      }
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
      render: (ip) => ip || '-'
    },
    {
      title: 'Changes',
      key: 'changes',
      width: 300,
      render: (_, record) => {
        if (!record.formattedChanges || record.formattedChanges.length === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>No changes tracked</span>;
        }
        
        return (
          <div style={{ fontSize: '12px' }}>
            {record.formattedChanges.slice(0, 2).map((change, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                <strong>{change.field}:</strong> {
                  change.action === 'changed' 
                    ? `"${change.oldValue}" → "${change.newValue}"`
                    : change.action === 'set'
                    ? `${change.newValue}`
                    : `${change.oldValue} (deleted)`
                }
              </div>
            ))}
            {record.formattedChanges.length > 2 && (
              <span style={{ color: '#1890ff' }}>
                +{record.formattedChanges.length - 2} more changes
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showDetails(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="Audit Trail" 
        subtitle="Track all system activities and changes"
        breadcrumbs={['Dashboard', 'Audit Trail']}
      />

      {/* Search and Filter Section */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
            
              placeholder="Search in changes (name, email, phone, etc.)"
              allowClear
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Entity Type"
              allowClear
              style={{ width: '100%' }}
              value={filters.entityType}
              onChange={(value) => setFilters(prev => ({ ...prev, entityType: value }))}
            >
              <Option value="User">User</Option>
              <Option value="Group">Group</Option>
              <Option value="BusinessUnit">Business Unit</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Action"
              allowClear
              style={{ width: '100%' }}
              value={filters.action}
              onChange={(value) => setFilters(prev => ({ ...prev, action: value }))}
            >
              <Option value="CREATE">CREATE</Option>
              <Option value="CREATE-BULK">CREATE-BULK</Option>
              <Option value="UPDATE">UPDATE</Option>
              <Option value="DELETE">DELETE</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space style={{ width: '100%' }}>
              <RangePicker
                showTime={{
                  format: 'HH:mm',
                }}
                format="YYYY-MM-DD HH:mm"
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                placeholder={['Start Date & Time', 'End Date & Time']}
               
              />
              <Button 
                icon={<SearchOutlined />}
                onClick={handleSearch}
                
              >
                Search
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Audit Trail Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={Array.isArray(auditData) ? auditData : []}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          onChange={handleTableChange}
          rowKey="_id"
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title="Audit Trail Details"
        visible={detailsModal.visible}
        onCancel={() => setDetailsModal({ visible: false, record: null })}
        footer={[
          <Button key="close" onClick={() => setDetailsModal({ visible: false, record: null })}>
            Close
          </Button>
        ]}
        width={800}
      >
        {detailsModal.record && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Timestamp" span={2}>
              {formatDate(detailsModal.record.timestamp)}
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              <Tag color={getActionColor(detailsModal.record.action)}>
                {detailsModal.record.action}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Entity Type">
              <Tag color={getEntityTypeColor(detailsModal.record.entityType)}>
                {detailsModal.record.entityType}
              </Tag>
            </Descriptions.Item>
            {detailsModal.record.entityDescription ? (
              <Descriptions.Item label="Entity" span={2}>
                <strong>{detailsModal.record.entityDescription}</strong>
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Entity ID" span={2}>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {detailsModal.record.entityId}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Performed By" span={2}>
              {typeof detailsModal.record.performedBy === 'object' && detailsModal.record.performedBy
                ? `${detailsModal.record.performedBy.name} ${detailsModal.record.performedBy.surname} (${detailsModal.record.performedBy.email})`
                : detailsModal.record.performedBy || 'System'
              }
            </Descriptions.Item>
            <Descriptions.Item label="IP Address">
              {detailsModal.record.ipAddress || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="User Agent" span={2}>
              {detailsModal.record.userAgent || '-'}
            </Descriptions.Item>
            {detailsModal.record.description && (
              <Descriptions.Item label="Description" span={2}>
                <strong>{detailsModal.record.description}</strong>
              </Descriptions.Item>
            )}
            {detailsModal.record.formattedChanges && detailsModal.record.formattedChanges.length > 0 && (
              <Descriptions.Item label="Changes" span={2}>
                <div style={{ background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
                  {detailsModal.record.formattedChanges.map((change, index) => (
                    <div key={index} style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <strong>{change.field}:</strong> {
                        change.action === 'changed' 
                          ? (
                            <span>
                              <span style={{ background: '#ffebe6', padding: '2px 4px', borderRadius: '2px' }}>
                                "{change.oldValue}"
                              </span>
                              <span style={{ margin: '0 8px', color: '#666' }}>→</span>
                              <span style={{ background: '#e6fffb', padding: '2px 4px', borderRadius: '2px' }}>
                                "{change.newValue}"
                              </span>
                            </span>
                          )
                          : change.action === 'set'
                          ? (
                            <span style={{ background: '#e6fffb', padding: '2px 4px', borderRadius: '2px' }}>
                              {change.newValue}
                            </span>
                          )
                          : (
                            <span style={{ background: '#ffebe6', padding: '2px 4px', borderRadius: '2px' }}>
                              {change.oldValue} (deleted)
                            </span>
                          )
                      }
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AuditTrail;